import React from "react";
import {
  Disc,
  Droplets,
  Gauge,
  Cog,
  CarFront,
  BatteryCharging,
  ThermometerSun,
  ClipboardCheck,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/lib/business";

const ICON_MAP = {
  Disc,
  Droplets,
  Gauge,
  Cog,
  CarFront,
  BatteryCharging,
  ThermometerSun,
  ClipboardCheck,
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D32F2F]" />
            <span className="font-mono-label text-[#D32F2F]">Full-Service Capabilities</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            Complete Auto Repair, Done{" "}
            <span className="text-[#D32F2F]">Right The First Time.</span>
          </h2>
          <p className="mt-5 text-[#4A5568] text-lg leading-relaxed">
            From a quick oil change to a complete transmission rebuild — our
            ASE-certified team services every major system on domestic and
            import vehicles.
          </p>
        </div>

        {/* Technical grid with border-collapse */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E2E8F0]">
          {SERVICES.map((s, idx) => {
            const Icon = ICON_MAP[s.icon] || Cog;
            return (
              <div
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="group relative border-r border-b border-[#E2E8F0] p-7 md:p-8 bg-white hover:bg-[#0B132B] transition-colors duration-300 cursor-pointer"
              >
                {/* Index label */}
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono-label text-[#4A5568] group-hover:text-[#D32F2F] transition-colors">
                    /{String(idx + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-[#E2E8F0] group-hover:text-[#D32F2F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>

                <Icon
                  className="w-9 h-9 text-[#D32F2F] mb-6"
                  strokeWidth={2}
                />
                <h3 className="font-display text-xl md:text-2xl font-semibold text-[#0B132B] group-hover:text-white transition-colors">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-[#4A5568] group-hover:text-white/80 leading-relaxed transition-colors">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA row */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-[#E2E8F0] pt-8">
          <p className="text-[#4A5568]">
            Don't see what you need?{" "}
            <span className="text-[#0B132B] font-semibold">If it drives, we fix it.</span>
          </p>
          <a
            href="#contact"
            data-testid="services-cta-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#0B132B] hover:bg-[#D32F2F] text-white font-semibold py-3 px-7 rounded-none transition-colors self-start md:self-auto"
          >
            Request a Free Estimate
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </section>
  );
}
